import { supabase } from '@/lib/supabase';
import type {
  Milestone,
  MilestoneStageStatus,
  MilestoneStatus,
  Priority,
  Project,
  ProjectStatus,
  ProjectType,
  Task,
  TaskStatus,
} from '@/types';
import type { ProjectStructuringAuditStatus } from '@/services/project-structuring/ProjectStructuringAuditService';
import type { ProjectCapitalInvestorOverview } from '@/services/project-structuring/ProjectCapitalSetupService';

interface RpcError {
  message: string;
}

interface RpcResponse {
  data: unknown;
  error: RpcError | null;
}

export interface ProjectPersistenceRpcClient {
  rpc(
    functionName: string,
    args?: Record<string, unknown>,
  ): PromiseLike<RpcResponse>;
}

export interface CommitProjectStructuringDraftInput {
  draftId: string;
  expectedVersion: number;
  idempotencyKey: string;
}

export interface CommitProjectStructuringDraftResult {
  projectId: string;
  createdMilestoneIds: string[];
  draftVersion: number;
  idempotencyKey: string;
  auditStatus: ProjectStructuringAuditStatus;
  auditReceipt: ProjectAuditReceipt;
  capitalSetupStatus: 'not_configured' | 'created' | 'requires_attention';
  capitalRelationshipId?: string;
}

export interface ProjectAuditReceipt {
  id: string;
  sequenceNumber: number;
  previousEventHash: string | null;
  eventHash: string;
  occurredAt: string;
}

export interface ProjectAuditChainVerification {
  valid: boolean;
  checkedEvents: number;
  invalidEventId?: string;
  invalidSequenceNumber?: number;
}

export interface ProductionProjectSnapshot {
  project: Project;
  milestones: Milestone[];
  tasks: Task[];
  memberships: Array<Record<string, unknown>>;
  companies: Array<Record<string, unknown>>;
  capital: {
    rounds: Array<Record<string, unknown>>;
    investments: Array<Record<string, unknown>>;
    positions: Array<Record<string, unknown>>;
  };
}

const PROJECT_TYPES: readonly ProjectType[] = [
  'consulting',
  'go_to_market',
  'capital_raising',
  'marketing',
  'sales',
  'business_development',
  'market_intelligence',
  'manufacturing',
  'co_manufacturing',
  'recruitment',
  'development',
  'legal',
  'design',
];

const PROJECT_PRIORITIES: readonly Priority[] = ['low', 'medium', 'high', 'critical'];
const TASK_STATUSES: readonly TaskStatus[] = ['todo', 'in_progress', 'review', 'completed'];
const PROJECT_STATUSES: readonly ProjectStatus[] = [
  'planning',
  'active',
  'on_hold',
  'completed',
  'cancelled',
  'archived',
  'trashed',
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function records(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

function stringValue(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function numberValue(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

function dateValue(value: unknown, fallback = new Date(0)): Date {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value === 'string') {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return fallback;
}

function metadataOf(row: Record<string, unknown>): Record<string, unknown> {
  return isRecord(row.metadata) ? row.metadata : {};
}

function nullableString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function booleanValue(value: unknown): boolean {
  return value === true;
}

function isSha256(value: unknown): value is string {
  return typeof value === 'string' && /^[0-9a-f]{64}$/.test(value);
}

function projectTypeOf(value: unknown): ProjectType {
  return PROJECT_TYPES.includes(value as ProjectType) ? value as ProjectType : 'consulting';
}

function projectPriorityOf(value: unknown): Priority {
  return PROJECT_PRIORITIES.includes(value as Priority) ? value as Priority : 'medium';
}

function projectStatusOf(value: unknown): ProjectStatus {
  return PROJECT_STATUSES.includes(value as ProjectStatus) ? value as ProjectStatus : 'planning';
}

function taskStatusOf(value: unknown): TaskStatus {
  return TASK_STATUSES.includes(value as TaskStatus) ? value as TaskStatus : 'todo';
}

function milestoneStatusOf(
  status: unknown,
  dueDate: Date,
): { status: MilestoneStatus; stageStatus: MilestoneStageStatus } {
  switch (status) {
    case 'completed':
      return { status: 'completed', stageStatus: 'Complete' };
    case 'in_progress':
      return { status: 'active', stageStatus: 'In Progress' };
    case 'in_review':
      return { status: 'active', stageStatus: 'In Review' };
    case 'blocked':
      return { status: 'active', stageStatus: 'Blocked' };
    default:
      return dueDate.getTime() < Date.now()
        ? { status: 'overdue', stageStatus: 'Not Started' }
        : { status: 'upcoming', stageStatus: 'Not Started' };
  }
}

function mapTask(row: Record<string, unknown>): Task {
  const metadata = metadataOf(row);
  const createdAt = dateValue(row.created_at);
  const updatedAt = dateValue(row.updated_at, createdAt);
  const dueDate = dateValue(row.due_date, updatedAt);
  const tags = Array.isArray(row.tags)
    ? row.tags.filter((tag): tag is string => typeof tag === 'string')
    : [];

  return {
    id: stringValue(row.id),
    title: stringValue(row.title, 'Untitled Task'),
    description: stringValue(row.description),
    status: taskStatusOf(row.status),
    priority: projectPriorityOf(row.priority),
    assigneeId: stringValue(row.assignee_id),
    createdBy: optionalString(row.created_by),
    assignedBy: optionalString(row.assigned_by),
    projectId: stringValue(row.project_id),
    milestoneId: optionalString(row.milestone_id),
    estimatedHours: numberValue(metadata.estimatedHours),
    dueDate,
    tags,
    attachments: Array.isArray(metadata.attachments)
      ? metadata.attachments.filter((item): item is string => typeof item === 'string')
      : [],
    comments: [],
    visibility: metadata.visibility === 'client'
      || metadata.visibility === 'partner'
      || metadata.visibility === 'private'
      || metadata.visibility === 'custom'
      ? metadata.visibility
      : 'internal',
    visibleToUserIds: Array.isArray(metadata.visibleToUserIds)
      ? metadata.visibleToUserIds.filter((id): id is string => typeof id === 'string')
      : undefined,
    createdAt,
    updatedAt,
    sortOrder: numberValue(row.sort_order),
    measurement_mode: metadata.measurementMode === 'quantity' ? 'quantity' : 'time',
    unit: optionalString(metadata.unit),
    quantity_planned: numberValue(metadata.quantityPlanned),
    quantity_completed: numberValue(metadata.quantityCompleted),
    quantity_verified: numberValue(metadata.quantityVerified),
    capital_relevant: metadata.capitalRelevant === true,
    verification_status:
      metadata.verificationStatus === 'approved' || metadata.verificationStatus === 'rejected'
        ? metadata.verificationStatus
        : 'pending',
    governance_role:
      metadata.governanceRole === 'required'
      || metadata.governanceRole === 'weighted'
      || metadata.governanceRole === 'supporting'
      || metadata.governanceRole === 'blocker'
        ? metadata.governanceRole
        : undefined,
    governance_weight: numberValue(metadata.governanceWeight),
    validator_id: optionalString(metadata.validatorId),
  };
}

function datePayload(value: unknown): string | null | undefined {
  if (value === null) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? undefined : value.toISOString();
  return typeof value === 'string' ? value : undefined;
}

function taskPayload(task: Partial<Task>): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  const copyKeys: Array<keyof Task> = [
    'title', 'description', 'status', 'priority', 'assigneeId', 'milestoneId',
    'estimatedHours', 'tags', 'visibility', 'visibleToUserIds', 'measurement_mode',
    'unit', 'quantity_planned', 'quantity_completed', 'quantity_verified',
    'capital_relevant', 'verification_status', 'governance_role', 'governance_weight',
  ];
  for (const key of copyKeys) {
    if (Object.prototype.hasOwnProperty.call(task, key)) payload[key] = task[key];
  }
  if (Object.prototype.hasOwnProperty.call(task, 'dueDate')) payload.dueDate = datePayload(task.dueDate);
  return payload;
}

function milestonePayload(milestone: Partial<Milestone>): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  const copyKeys: Array<keyof Milestone> = ['name', 'description', 'status', 'deliverables'];
  for (const key of copyKeys) {
    if (Object.prototype.hasOwnProperty.call(milestone, key)) payload[key] = milestone[key];
  }
  if (Object.prototype.hasOwnProperty.call(milestone, 'startDate')) payload.startDate = datePayload(milestone.startDate);
  if (Object.prototype.hasOwnProperty.call(milestone, 'dueDate')) payload.dueDate = datePayload(milestone.dueDate);
  return payload;
}

function projectPayload(project: Partial<Project>): Record<string, unknown> {
  const requestedKeys = Object.keys(project);
  if (requestedKeys.length === 0) {
    throw new Error('Project update must include at least one supported field.');
  }

  const supportedKeys = new Set([
    'name', 'description', 'status', 'priority', 'startDate', 'endDate',
  ]);
  const unsupportedKeys = requestedKeys.filter((key) => !supportedKeys.has(key));
  if (unsupportedKeys.some((key) => key === 'budget' || key === 'spent')) {
    throw new Error(
      'Project budget and spend cannot be changed through a generic project update.',
    );
  }
  if (unsupportedKeys.length > 0) {
    throw new Error(
      `Unsupported generic project update field(s): ${unsupportedKeys.sort().join(', ')}.`,
    );
  }

  const payload: Record<string, unknown> = {};
  const copyKeys: Array<keyof Project> = [
    'name', 'description', 'status', 'priority',
  ];
  for (const key of copyKeys) {
    if (Object.prototype.hasOwnProperty.call(project, key)) payload[key] = project[key];
  }
  if (Object.prototype.hasOwnProperty.call(project, 'startDate')) payload.startDate = datePayload(project.startDate);
  if (Object.prototype.hasOwnProperty.call(project, 'endDate')) payload.endDate = datePayload(project.endDate);
  return payload;
}

function deliverableNames(metadata: Record<string, unknown>): string[] {
  if (!Array.isArray(metadata.deliverables)) return [];
  return metadata.deliverables.flatMap((deliverable) => {
    if (typeof deliverable === 'string') return [deliverable];
    if (isRecord(deliverable) && typeof deliverable.name === 'string') return [deliverable.name];
    return [];
  });
}

function mapMilestone(row: Record<string, unknown>, allTasks: Task[]): Milestone {
  const metadata = metadataOf(row);
  const createdAt = dateValue(row.created_at);
  const dueDate = dateValue(row.due_date, createdAt);
  const lifecycle = milestoneStatusOf(row.status, dueDate);
  const tasks = allTasks.filter((task) => task.milestoneId === stringValue(row.id));
  const completedTasks = tasks.filter((task) => task.status === 'completed').length;

  return {
    id: stringValue(row.id),
    name: stringValue(row.name ?? row.title, 'Untitled Milestone'),
    description: stringValue(row.description),
    projectId: stringValue(row.project_id),
    startDate: row.start_date ? dateValue(row.start_date) : undefined,
    dueDate,
    status: lifecycle.status,
    stageStatus: lifecycle.stageStatus,
    progress: tasks.length > 0 ? Math.round((completedTasks / tasks.length) * 100) : 0,
    tasks,
    deliverables: deliverableNames(metadata),
    assignedUsers: optionalString(row.owner_id) ? [stringValue(row.owner_id)] : [],
    createdAt,
    updatedAt: row.updated_at ? dateValue(row.updated_at, createdAt) : undefined,
  };
}

function mapProject(
  row: Record<string, unknown>,
  memberships: Array<Record<string, unknown>>,
  milestones: Milestone[],
): Project {
  const metadata = metadataOf(row);
  const managers = memberships.filter(
    (membership) => membership.project_role === 'Project Manager' && membership.status === 'active',
  );
  const clients = memberships.filter(
    (membership) => ['Client', 'Company'].includes(String(membership.project_role))
      && membership.status === 'active',
  );
  const persistedTeamMembers = Array.isArray(row.team_members)
    ? row.team_members.filter((member): member is string => typeof member === 'string')
    : [];
  const teamMembers = persistedTeamMembers.length > 0
    ? persistedTeamMembers
    : memberships
      .map((membership) => optionalString(membership.user_id))
      .filter((member): member is string => Boolean(member));
  const completedMilestones = milestones.filter((milestone) => milestone.status === 'completed').length;
  const createdAt = dateValue(row.created_at);
  const updatedAt = dateValue(row.updated_at, createdAt);

  return {
    id: stringValue(row.id),
    name: stringValue(row.name, 'Untitled Project'),
    description: stringValue(row.description),
    type: projectTypeOf(row.project_type),
    status: projectStatusOf(row.status),
    priority: projectPriorityOf(metadata.priority),
    startDate: dateValue(row.start_date, createdAt),
    endDate: row.target_date ? dateValue(row.target_date) : undefined,
    progress: milestones.length > 0
      ? Math.round((completedMilestones / milestones.length) * 100)
      : 0,
    budget: numberValue(row.allocated_capital),
    spent: numberValue(metadata.spentCapital),
    clientId: optionalString(clients[0]?.user_id),
    managerId: optionalString(managers[0]?.user_id),
    teamMembers,
    milestones,
    documents: [],
    dueDiligenceEnabled: row.due_diligence_enabled === true,
    createdAt,
    updatedAt,
  };
}

export function parseProjectAuditReceipt(value: unknown): ProjectAuditReceipt {
  if (!isRecord(value)) {
    throw new Error('Project creation did not return a sealed audit receipt.');
  }

  const id = stringValue(value.id);
  const sequenceNumber = numberValue(value.sequenceNumber);
  const previousEventHash = value.previousEventHash === null
    ? null
    : nullableString(value.previousEventHash);
  const eventHash = value.eventHash;
  const occurredAt = stringValue(value.occurredAt);
  const occurredAtDate = new Date(occurredAt);

  if (
    !id
    || !Number.isInteger(sequenceNumber)
    || sequenceNumber < 1
    || !isSha256(eventHash)
    || (previousEventHash !== null && !isSha256(previousEventHash))
    || !occurredAt
    || Number.isNaN(occurredAtDate.getTime())
  ) {
    throw new Error('Project creation returned an invalid sealed audit receipt.');
  }

  return {
    id,
    sequenceNumber,
    previousEventHash,
    eventHash,
    occurredAt,
  };
}

function parseAuditChainVerification(value: unknown): ProjectAuditChainVerification {
  if (!isRecord(value) || typeof value.valid !== 'boolean') {
    throw new Error('Audit verification returned an invalid result.');
  }

  const checkedEvents = numberValue(value.checkedEvents, -1);
  if (!Number.isInteger(checkedEvents) || checkedEvents < 0) {
    throw new Error('Audit verification returned an invalid event count.');
  }

  const invalidSequenceNumber = value.invalidSequenceNumber === undefined
    || value.invalidSequenceNumber === null
    ? undefined
    : numberValue(value.invalidSequenceNumber, -1);
  if (
    invalidSequenceNumber !== undefined
    && (!Number.isInteger(invalidSequenceNumber) || invalidSequenceNumber < 1)
  ) {
    throw new Error('Audit verification returned an invalid sequence number.');
  }

  return {
    valid: value.valid,
    checkedEvents,
    invalidEventId: optionalString(value.invalidEventId),
    invalidSequenceNumber,
  };
}

function parseCommitResult(value: unknown): CommitProjectStructuringDraftResult {
  if (!isRecord(value)) {
    throw new Error('Project creation returned an invalid result.');
  }

  const projectId = stringValue(value.projectId);
  const idempotencyKey = stringValue(value.idempotencyKey);
  const draftVersion = numberValue(value.draftVersion);
  if (!projectId || !idempotencyKey || draftVersion < 1) {
    throw new Error('Project creation did not return a durable Project receipt.');
  }
  if (value.auditStatus !== 'recorded') {
    throw new Error('Project creation did not commit an immutable audit event.');
  }

  const capitalSetupStatus =
    value.capitalSetupStatus === 'created'
    || value.capitalSetupStatus === 'requires_attention'
    || value.capitalSetupStatus === 'not_configured'
      ? value.capitalSetupStatus
      : 'not_configured';

  return {
    projectId,
    createdMilestoneIds: Array.isArray(value.createdMilestoneIds)
      ? value.createdMilestoneIds.filter((id): id is string => typeof id === 'string')
      : [],
    draftVersion,
    idempotencyKey,
    auditStatus: 'recorded',
    auditReceipt: parseProjectAuditReceipt(value.auditReceipt),
    capitalSetupStatus,
    capitalRelationshipId: optionalString(value.capitalRelationshipId),
  };
}

function parseSnapshot(value: unknown): ProductionProjectSnapshot {
  if (!isRecord(value) || !isRecord(value.project)) {
    throw new Error('Project snapshot was not found.');
  }

  const memberships = records(value.memberships);
  const taskRows = records(value.tasks);
  const milestoneRows = records(value.milestones);
  const tasks = taskRows.map(mapTask);
  const milestones = milestoneRows.map((row) => mapMilestone(row, tasks));
  const rawCapital = isRecord(value.capital) ? value.capital : {};

  return {
    project: mapProject(value.project, memberships, milestones),
    milestones,
    tasks,
    memberships,
    companies: records(value.companies),
    capital: {
      rounds: records(rawCapital.rounds),
      investments: records(rawCapital.investments),
      positions: records(rawCapital.positions),
    },
  };
}

function latestRecord(
  values: Array<Record<string, unknown>>,
): Record<string, unknown> | undefined {
  return values[values.length - 1];
}

export function projectCapitalOverviewFromSnapshot(
  snapshot: ProductionProjectSnapshot,
): ProjectCapitalInvestorOverview | null {
  const round = latestRecord(
    snapshot.capital.rounds.filter((candidate) => candidate.status !== 'cancelled'),
  );
  if (!round) return null;

  const roundId = stringValue(round.id);
  const investments = snapshot.capital.investments.filter(
    (investment) =>
      stringValue(investment.round_id) === roundId
      && investment.status !== 'cancelled',
  );
  const investment = latestRecord(investments);
  if (!roundId || !investment) return null;

  const investmentId = stringValue(investment.id);
  const positions = snapshot.capital.positions.filter(
    (position) =>
      stringValue(position.investment_id) === investmentId
      && position.status !== 'cancelled',
  );
  const totalCapitalRequired = numberValue(round.target_amount);
  const totalCommitted = positions.reduce(
    (total, position) => total + numberValue(position.commitment_amount),
    0,
  );

  return {
    projectId: snapshot.project.id,
    workspaceId: stringValue(round.workspace_id),
    issuerCompanyId: stringValue(round.issuer_company_id),
    roundId,
    investmentId,
    roundName: stringValue(round.name, 'Project capital'),
    currency: stringValue(round.currency, 'EUR'),
    totalCapitalRequired,
    totalCommitted,
    fundingGap: Math.max(0, totalCapitalRequired - totalCommitted),
    investors: positions.map((position) => {
      const metadata = metadataOf(position);
      return {
        positionId: stringValue(position.id),
        investmentId,
        investorCompanyId: stringValue(position.investor_company_id),
        investorCompanyName:
          optionalString(metadata.investorCompanyName)
          ?? stringValue(position.position_name, 'Investor'),
        commitmentAmount: numberValue(position.commitment_amount),
        ownershipPercent: position.ownership_percent === null
          || position.ownership_percent === undefined
          ? null
          : numberValue(position.ownership_percent),
        votingPercent: metadata.votingPercent === null
          || metadata.votingPercent === undefined
          ? null
          : numberValue(metadata.votingPercent),
        approvalExpected:
          booleanValue(metadata.approvalExpected)
          || metadata.authorityRequirement === 'approval'
          || metadata.authorityRequirement === 'approval_and_signature',
        signatureExpected:
          booleanValue(metadata.signatureExpected)
          || metadata.authorityRequirement === 'signature'
          || metadata.authorityRequirement === 'approval_and_signature',
        primaryContactUserId: nullableString(position.primary_contact_user_id),
        status:
          position.status === 'draft'
          || position.status === 'active'
          || position.status === 'on_hold'
          || position.status === 'realized'
          || position.status === 'exited'
          || position.status === 'cancelled'
            ? position.status
            : 'committed',
      };
    }),
    createsGateAuthority: false,
  };
}

export class ProductionProjectPersistenceService {
  constructor(private readonly client: ProjectPersistenceRpcClient) {}

  async commitProjectStructuringDraft(
    input: CommitProjectStructuringDraftInput,
  ): Promise<CommitProjectStructuringDraftResult> {
    const { data, error } = await this.client.rpc(
      'ax1_create_project_from_structuring_draft',
      {
        p_draft_id: input.draftId,
        p_expected_version: input.expectedVersion,
        p_idempotency_key: input.idempotencyKey,
      },
    );

    if (error) {
      throw new Error(`Project creation failed: ${error.message}`);
    }
    return parseCommitResult(data);
  }

  async getProjectSnapshot(projectId: string): Promise<ProductionProjectSnapshot> {
    const { data, error } = await this.client.rpc('ax1_get_project_snapshot', {
      p_project_id: projectId,
    });

    if (error) {
      throw new Error(`Project loading failed: ${error.message}`);
    }
    return parseSnapshot(data);
  }

  async getProjectCapitalInvestorOverview(
    projectId: string,
  ): Promise<ProjectCapitalInvestorOverview | null> {
    const snapshot = await this.getProjectSnapshot(projectId);
    return projectCapitalOverviewFromSnapshot(snapshot);
  }

  async listProjects(): Promise<Project[]> {
    const { data, error } = await this.client.rpc('ax1_list_project_summaries');
    if (error) {
      throw new Error(`Project list loading failed: ${error.message}`);
    }
    return records(data).map((row) => mapProject(row, [], []));
  }

  async listProjectSnapshots(): Promise<ProductionProjectSnapshot[]> {
    const { data, error } = await this.client.rpc('ax1_list_project_snapshots');
    if (error) {
      throw new Error(`Project snapshot loading failed: ${error.message}`);
    }
    return Array.isArray(data) ? data.map(parseSnapshot) : [];
  }

  async createTask(
    task: Omit<Task, 'id' | 'createdAt' | 'updatedAt' | 'comments'> & Partial<Pick<Task, 'comments'>>,
  ): Promise<Task> {
    const { data, error } = await this.client.rpc('ax1_create_task_secure', {
      p_project_id: task.projectId,
      p_payload: taskPayload(task),
    });
    if (error) throw new Error(`Task creation failed: ${error.message}`);
    if (!isRecord(data)) throw new Error('Task creation returned an invalid record.');
    return mapTask(data);
  }

  async updateTask(taskId: string, updates: Partial<Task>): Promise<Task> {
    const { data, error } = await this.client.rpc('ax1_update_task_secure', {
      p_task_id: taskId,
      p_payload: taskPayload(updates),
    });
    if (error) throw new Error(`Task update failed: ${error.message}`);
    if (!isRecord(data)) throw new Error('Task update returned an invalid record.');
    return mapTask(data);
  }

  async deleteTask(taskId: string): Promise<boolean> {
    const { data, error } = await this.client.rpc('ax1_delete_task_secure', { p_task_id: taskId });
    if (error) throw new Error(`Task deletion failed: ${error.message}`);
    return data === true;
  }

  async createMilestone(milestone: Omit<Milestone, 'id' | 'createdAt'>): Promise<Milestone> {
    const { data, error } = await this.client.rpc('ax1_create_milestone_secure', {
      p_project_id: milestone.projectId,
      p_payload: milestonePayload(milestone),
    });
    if (error) throw new Error(`Milestone creation failed: ${error.message}`);
    if (!isRecord(data)) throw new Error('Milestone creation returned an invalid record.');
    return mapMilestone(data, []);
  }

  async updateMilestone(milestoneId: string, updates: Partial<Milestone>): Promise<Milestone> {
    const { data, error } = await this.client.rpc('ax1_update_milestone_secure', {
      p_milestone_id: milestoneId,
      p_payload: milestonePayload(updates),
    });
    if (error) throw new Error(`Milestone update failed: ${error.message}`);
    if (!isRecord(data)) throw new Error('Milestone update returned an invalid record.');
    return mapMilestone(data, []);
  }

  async deleteMilestone(milestoneId: string): Promise<boolean> {
    const { data, error } = await this.client.rpc('ax1_delete_milestone_secure', { p_milestone_id: milestoneId });
    if (error) throw new Error(`Milestone deletion failed: ${error.message}`);
    return data === true;
  }

  async updateProject(projectId: string, updates: Partial<Project>): Promise<Project> {
    const payload = projectPayload(updates);
    const { data, error } = await this.client.rpc('ax1_update_project_secure', {
      p_project_id: projectId,
      p_payload: payload,
    });
    if (error) throw new Error(`Project update failed: ${error.message}`);
    if (data !== true) throw new Error('Project update was not applied.');
    return (await this.getProjectSnapshot(projectId)).project;
  }

  async verifyAuditChain(workspaceId: string): Promise<ProjectAuditChainVerification> {
    const { data, error } = await this.client.rpc('ax1_verify_audit_chain', {
      p_workspace_id: workspaceId,
    });
    if (error) {
      throw new Error(`Audit verification failed: ${error.message}`);
    }
    return parseAuditChainVerification(data);
  }
}

export const productionProjectPersistence = supabase
  ? new ProductionProjectPersistenceService(supabase as ProjectPersistenceRpcClient)
  : null;
